import Post from './Post'

/**
 * Formats, sorts, filters a post and its children to create a list.
 * For example, can be used to create menus.
 */
export default class PostList {
    constructor(root, options) {
        this.rootItem = root
        this.setOptions(options)
        this.createList()
    }

    /**
     * Sets the options to create the list.
     *
     * @param {object} options
     */
    setOptions(options) {
        this.propertyToSortBy = options?.sortBy ?? ''
        this.filters = options?.filters ?? []

        this.isMenu = options?.isMenu ?? false

        // enables filtering by hideFromMenu and sorts by menuOrder
        if (this.isMenu) {
            this.filters.push(this.filterByProperty('hideFromMenu', false))
            if (this.propertyToSortBy === '') {
                this.propertyToSortBy = 'menuOrder'
            }
        }

        this.depth = options?.depth ?? 2
    }

    /**
     * Returns the post list based on the current options set.
     * @returns
     */
    getList() {
        return this.list
    }

    /**
     * Create the list based on the current options.
     */
    createList() {
        var currentLevel = 0
        var rootItem = { ...this.rootItem }

        // Add children based on the desired depth
        rootItem.children = this.addChildren(
            this.rootItem?.wpChildren?.nodes,
            currentLevel + 1,
            this.depth,
        )

        this.list = rootItem
    }

    /**
     * Maps through the children if the current depth is still below the max depth.
     *
     * @param {array} children An array of chilren
     * @param {number} currentLevel The current depth of children
     * @param {number} maxLevel The max depth to add children
     * @returns array
     */
    addChildren(children, currentLevel, maxLevel) {
        if (!children || currentLevel > maxLevel) return []

        const childrenList = children.map(child => {
            const childPost = new Post(child)
            return {
                ...childPost.getPost(),
                children: this.addChildren(
                    child?.wpChildren?.nodes,
                    currentLevel + 1,
                    maxLevel,
                ),
            }
        })

        /**
         * Filter the children based on the provided options.
         */
        const filteredChildren = this.filters.reduce(
            (children, filterFn) => children.filter(filterFn),
            childrenList,
        )

        /**
         * Sort the children based on the provided options.
         */
        return filteredChildren.sort(this.sortByProperty(this.propertyToSortBy))
    }

    /**
     * Sorty by a specific property on the post.
     *
     * @param {string} property The property to sort by
     * @returns
     */
    sortByProperty(property) {
        return function sort(item1, item2) {
            if (
                item1?.[property] === undefined ||
                !item2?.[property] === undefined
            ) {
                return 0
            }
            if (item1[property] < item2[property]) {
                return -1
            }
            if (item1[property] > item2[property]) {
                return 1
            }
            return 0
        }
    }

    /**
     * Filter the post by a specific property/value.
     *
     * @param {string} property The property to filter by
     * @param {*} value The value to check
     * @returns array
     */
    filterByProperty(property, value) {
        return function filterItemByProperty(post) {
            return post?.[property] === value
        }
    }
}
